import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { eqlAggregate } from "../../../../apis/apiEQL";
import { CHART_BLUE, CHART_COLORS, CHART_NAVY } from "../../chartPalette";

// Ventanas móviles (en días) de la sección "Actividad en reportes".
const WINDOWS = [7, 30, 60, 90];

// Un color de la paleta por ventana: de más reciente (azul) a más amplia (marino).
const WINDOW_COLOR = {
  7: CHART_BLUE, // #2563eb
  30: CHART_COLORS[2], // Cian
  60: CHART_COLORS[3], // Teal
  90: CHART_NAVY, // #1e3a8a
};

/**
 * Día calendario local desplazado `days` días, en "YYYY-MM-DD".
 * Se usa como corte de rango: `gte` incluye el día, `lt` lo deja fuera.
 */
const dayOffset = (days) => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + days);
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${mm}-${dd}`;
};

// Rango semiabierto [desde, hasta) sobre created_at, en días relativos a hoy.
const rangeFilter = (fromDays, toDays) => ({
  AND: [
    { field: "created_at", operator: "gte", value: dayOffset(fromDays) },
    { field: "created_at", operator: "lt", value: dayOffset(toDays) },
  ],
});

/**
 * Cuenta reportes del rango, sin borradores. El agregado por estatus con
 * `exclude: ["draft"]` es el mismo mecanismo que usan los demás indicadores;
 * `total_shown` es la suma de los grupos que quedaron.
 */
const countReports = async (filters) => {
  const resp = await eqlAggregate({
    groupBy: "status.current",
    exclude: ["draft"],
    filters,
  });
  return resp?.metadata?.total_shown ?? 0;
};

/**
 * useReportActivity — casos recibidos en las ventanas móviles de 7/30/60/90
 * días, cada una con su delta contra el periodo inmediato anterior de la misma
 * longitud (7 días vs. los 7 días previos, etc.).
 *
 * Ambas ventanas son rangos semiabiertos y contiguos, así que ningún reporte
 * se cuenta dos veces ni se pierde en la frontera. El día de hoy entra completo.
 *
 * Los cortes se calculan con el calendario local del navegador; el backend
 * guarda `created_at` en su propia zona (America/Chicago), así que en las
 * primeras horas del día un reporte puede caer de un lado u otro del corte.
 * Para una ventana de 7 días o más el efecto es despreciable.
 *
 * @returns {{ activity: Array<{id,days,label,value,delta,from,to,accentColor,iconName}>, loading: boolean }}
 */
export const useReportActivity = () => {
  const [t] = useTranslation("dashboard");
  const tRef = useRef(t);
  tRef.current = t;

  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;

    (async () => {
      setLoading(true);
      try {
        // Por ventana: [actual, anterior]. Todo en paralelo, son conteos.
        const counts = await Promise.all(
          WINDOWS.flatMap((n) => [
            countReports(rangeFilter(-(n - 1), 1)),
            countReports(rangeFilter(-(2 * n - 1), -(n - 1))),
          ])
        );
        if (!active) return;

        setActivity(
          WINDOWS.map((n, i) => {
            const current = counts[i * 2];
            const previous = counts[i * 2 + 1];
            return {
              id: `last-${n}d`,
              days: n,
              label: tRef.current("static_dashboard.activity_last_days", { count: n }),
              value: current,
              delta: current - previous,
              // Rango del periodo actual, para el enlace a la lista de reportes.
              from: dayOffset(-(n - 1)),
              to: dayOffset(0),
              accentColor: WINDOW_COLOR[n],
              iconName: "calendar_bold",
            };
          })
        );
      } catch (e) {
        console.error("useReportActivity: EQL aggregate failed", e);
        if (active) setActivity([]);
      } finally {
        if (active) setLoading(false);
      }
    })();

    return () => {
      active = false;
    };
  }, []);

  return { activity, loading };
};

export default useReportActivity;
